import { Type, type Static } from "@sinclair/typebox";
import type { SessionPaymentIntegration } from "./payment_integrations";
import { RestaurantSetting } from "./restaurant_settings";

/** A payment terminal assigned to the signed-in user (from get_user). */
export type SessionPaymentTerminal = {
  guid: string;
  name: string;
  status?: string;
  /** "nakka" for rows created before terminals had a provider. */
  provider?: string;
  /** Payment integration (credentials) the terminal is paired through. */
  integration_id?: string | null;
};

export type UserRole = "staff" | "manager" | "admin";
export interface RestaurantProfile {
  id: number;
  guid: string;
  name: string;
  slug: string;
  email: string;
  phone: string;
  contact_person?: string;
  address: string;
  receipt_logo?: string;
  welcome_screen_image?: string;
  receipt_phone: string;
  receipt_message: string;
  company_registration: string;
  food_receipt: number;
  bar_receipt: number;
  payrexx_name?: string;
  timezone: string;
  currency: string;
  locale: string;
  jurisdiction: string;
  description?: string;
  group_restaurants_ids?: string[];
  socials?: {
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    website?: string;
  };
  geolocation?: {
    lat?: number;
    lng?: number;
    address?: string;
  };
  feature_flags?: LemmonFeatureFlags;
  google_place_id?: string | null;
  google_rating?: number | null;
  google_review_count?: number | null;
  google_rating_fetched_at?: string | null;
}

export const LemmonFeatureFlagsSchema = Type.Object({
  la_suite_default_on: Type.Optional(Type.Boolean()),
  payment_integration_v2: Type.Optional(Type.Boolean()),
  // Show the POS tip screen before tap-to-pay (SoftPOS) payments.
  payment_integration_v2_tips: Type.Optional(Type.Boolean()),
  // Kill switch: run Nakka terminal payments the old way, in the browser
  // (legacy TerminalPaymentModal), instead of the integrated payment workflow
  // (DBOS / Restate / Cloudflare) they use by default.
  nakka_browser: Type.Optional(Type.Boolean()),
  mobile_pos_only_dine_in: Type.Optional(Type.Boolean()),
  "discounts-v2": Type.Optional(Type.Boolean()),
  print_job_tracking: Type.Optional(Type.Boolean()),
  // Print kitchen tickets through the native app (printEpos) on app builds
  // that support it. Our per-restaurant switch for rollout and rollback.
  native_print: Type.Optional(Type.Boolean()),
  // Route Payrexx ECR terminal calls to the new platform host
  // (api.lemmon-pos.ch) instead of the legacy pay.thelemmon.ch proxy (default).
  payrexx_new_platform: Type.Optional(Type.Boolean()),
});
//
export type LemmonFeatureFlags = Static<typeof LemmonFeatureFlagsSchema>;

export type Colleague = {
  id: number;
  role: UserRole;

  profile: {
    id: number;
    name: string;
    staff_type: string;
    status: "active" | "pending";
    deleted_at?: any;
  };
};

export type CurrentUser = {
  id: number;
  restaurant_id: number;
  // todo: role: UserRole
  shop_ids?: number[];
  // Lightweight subset returned by get_auth_user for identifying the user
  // (e.g. Sentry attribution). Full details live on `User` via get_user.
  profile: { id: number; name: string };
  restaurant: { id: number; name: string };
};

export type User = {
  id: number;
  restaurant_id: number;
  role: UserRole;
  profile: UserProfile;
  restaurant: RestaurantProfile;
  colleagues: Colleague[];
  shop_ids?: number[];
  payment_terminals?: SessionPaymentTerminal[];
  payment_integrations?: SessionPaymentIntegration[];
  restaurant_settings: RestaurantSetting[];
};
export interface UserProfile {
  id: number;
  name: string;
  guid: string;
  email: string;
  phone: string | null;
  status: "active" | "pending";
  deleted_at?: string | null;
  staff_type: string;
  restaurant_id: number;
  print_receipt: number;
}

// Staff management types
export const STAFF_TYPES = ["Front of house", "Kitchen", "Bar", "Delivery"] as const;
export type StaffType = (typeof STAFF_TYPES)[number];

/**
 * Map a stored staff_type to one of STAFF_TYPES. Older rows hold legacy
 * spellings ("kitchen", "front_of_house") or null; returns null when the
 * value can't be mapped, meaning "leave it as it is".
 */
export const normalizeStaffType = (value: unknown): StaffType | null => {
  if (typeof value !== "string") return null;
  const key = value.trim().toLowerCase().replace(/[_-]+/g, " ");
  return STAFF_TYPES.find((type) => type.toLowerCase() === key) ?? null;
};

export interface Staff {
  id: number;
  name: string;
  email: string | null;
  staff_type: string;
  status: string;
  role: UserRole;
  restaurant_id: number;
  print_receipt: number;
  created_at: string;
  updated_at: string;
}

export interface CreateStaffData {
  name: string;
  email?: string | null;
  password?: string | null;
  pin?: string;
  staff_type?: "Front of house" | "Kitchen" | "Bar" | "Delivery";
  role_name?: "staff" | "manager";
  print_receipt?: number;
}

export interface UpdateStaffData {
  name?: string;
  email?: string | null;
  password?: string;
  pin?: string;
  staff_type?: "Front of house" | "Kitchen" | "Bar" | "Delivery";
  role_name?: "staff" | "manager";
  print_receipt?: number;
  status?: "active" | "inactive";
}

export interface StaffFilters {
  search?: string;
  staff_type?: "Front of house" | "Kitchen" | "Bar" | "Delivery";
  limit?: number;
  offset?: number;
}
